// Exports: GIF (WASM worker), WebM/MP4 (WebCodecs + mediabunny), PNG16 zip.

import {
  BufferTarget,
  Mp4OutputFormat,
  Output,
  VideoSample,
  VideoSampleSource,
  WebMOutputFormat,
  canEncodeVideo,
} from 'mediabunny';
import type { Depth16 } from '../types';
import { batchPool } from '../wasm/pool';
import type { FrameStore } from './frames';
import type { GifMsg, GifReply } from './gif.worker';
import GifWorker from './gif.worker.ts?worker';
import { writeZip } from './zip';

export type VideoFormat = 'webm' | 'mp4';

export function playbackOrder(n: number, loop: 'loop' | 'ping_pong'): number[] {
  const fwd = Array.from({ length: n }, (_, i) => i);
  if (loop === 'loop' || n < 3) return fwd;
  return [...fwd, ...fwd.slice(1, -1).reverse()];
}

export async function exportGif(
  store: FrameStore,
  fps: number,
  loop: 'loop' | 'ping_pong',
  onProgress: (done: number, total: number) => void,
  signal?: AbortSignal,
): Promise<Blob> {
  const worker = new GifWorker();
  const send = (msg: GifMsg, transfer: Transferable[] = []) =>
    new Promise<GifReply>((resolve, reject) => {
      worker.onmessage = (ev: MessageEvent<GifReply>) =>
        ev.data.type === 'error' ? reject(new Error(ev.data.message)) : resolve(ev.data);
      worker.onerror = (ev) => reject(new Error(ev.message));
      worker.postMessage(msg, transfer);
    });
  try {
    await send({ type: 'start', width: store.width, height: store.height, fps, repeat: true });
    // Global palette from evenly spaced frames.
    const samples = Math.min(8, store.count);
    for (let k = 0; k < samples; k++) {
      const px = store.rgba(Math.floor((k * store.count) / samples))!;
      await send({ type: 'train', rgba: new Uint8Array(px.buffer) }, [px.buffer]);
    }
    const order = playbackOrder(store.count, loop);
    for (let k = 0; k < order.length; k++) {
      if (signal?.aborted) throw new DOMException('aborted', 'AbortError');
      const px = store.rgba(order[k])!;
      await send({ type: 'frame', rgba: new Uint8Array(px.buffer) }, [px.buffer]);
      onProgress(k + 1, order.length);
    }
    const done = await send({ type: 'finish' });
    if (done.type !== 'done') throw new Error('GIF encoder did not finish');
    return new Blob([done.bytes as Uint8Array<ArrayBuffer>], { type: 'image/gif' });
  } finally {
    worker.terminate();
  }
}

function videoBitrate(w: number, h: number, fps: number): number {
  // Random-dot patterns are noise to a video codec: give it a generous budget.
  return Math.round(Math.min(40e6, Math.max(2e6, w * h * fps * 0.35)));
}

const CODEC: Record<VideoFormat, 'vp9' | 'avc'> = { webm: 'vp9', mp4: 'avc' };

export async function canExportVideo(format: VideoFormat, w: number, h: number, fps: number): Promise<boolean> {
  if (typeof VideoEncoder === 'undefined') return false;
  try {
    return await canEncodeVideo(CODEC[format], {
      width: w,
      height: h,
      bitrate: videoBitrate(w, h, fps),
      frameRate: fps,
    });
  } catch {
    return false;
  }
}

export async function exportVideo(
  store: FrameStore,
  fps: number,
  loop: 'loop' | 'ping_pong',
  format: VideoFormat,
  onProgress: (done: number, total: number) => void,
  signal?: AbortSignal,
): Promise<Blob> {
  const target = new BufferTarget();
  const output = new Output({
    format: format === 'webm' ? new WebMOutputFormat() : new Mp4OutputFormat({ fastStart: 'in-memory' }),
    target,
  });
  const source = new VideoSampleSource({
    codec: CODEC[format],
    bitrate: videoBitrate(store.width, store.height, fps),
    keyFrameInterval: 2,
  });
  output.addVideoTrack(source, { frameRate: fps });
  await output.start();
  const order = playbackOrder(store.count, loop);
  const dur = 1 / fps;
  try {
    for (let k = 0; k < order.length; k++) {
      if (signal?.aborted) throw new DOMException('aborted', 'AbortError');
      const px = store.rgba(order[k])!;
      const sample = new VideoSample(px, {
        format: 'RGBA',
        codedWidth: store.width,
        codedHeight: store.height,
        timestamp: k * dur,
        duration: dur,
      });
      await source.add(sample);
      sample.close();
      onProgress(k + 1, order.length);
    }
    await output.finalize();
  } catch (e) {
    await output.cancel().catch(() => {});
    throw e;
  }
  const mime = format === 'webm' ? 'video/webm' : 'video/mp4';
  return new Blob([target.buffer!], { type: mime });
}

/** Depth frames as a zip of 16-bit grayscale PNGs (frame_0000.png, ...). */
export async function exportDepthZip(
  frames: Depth16[],
  onProgress: (done: number, total: number) => void,
): Promise<Blob> {
  const pool = batchPool();
  let done = 0;
  const pngs = await Promise.all(
    frames.map((depth) =>
      pool.call('encodeDepth16Png', { depth }).then((b) => {
        onProgress(++done, frames.length);
        return b;
      }),
    ),
  );
  return writeZip(pngs.map((data, i) => ({ name: `frame_${String(i).padStart(4, '0')}.png`, data })));
}
